import { useEffect, useState } from 'react';
import { IoMdClose } from 'react-icons/io';
import { AiOutlinePlus, AiOutlineMinus } from 'react-icons/ai';
import { getIcon } from 'utils/icons';

const ProductViewModal = (props: {
    isOpen: any;
    onClose: any;
    product: any;
    newOrder: any;
    onAddProduct: any;
    onQuantityChange: any;
    hidePricesOnShop?: boolean;
}) => {
    const {
        isOpen,
        onClose,
        product,
        newOrder,
        onAddProduct,
        onQuantityChange,
        hidePricesOnShop = false,
    } = props;

    const [quantity, setQuantity] = useState<number | null>(null);
    const [newQuantity, setNewQuantity] = useState(1);
    const [selectedChild, setSelectedChild] = useState<any>(null);

    const hasChildren = product?.children && product.children.length > 0;

    useEffect(() => {
        if (!product) return;

        setSelectedChild(null);
        
        const productOrderInfo = newOrder.items.find(
            (i: any) => i.productId === product.id
        );

        if (productOrderInfo) {
            setNewQuantity(productOrderInfo.quantity);
            setQuantity(productOrderInfo.quantity);
        } else {
            setQuantity(null);
            setNewQuantity(1);
        }
    }, [product, newOrder]);

    const handleChildSelect = (childId: string) => {
        const child = product.children.find((c: any) => c.id === childId);
        if (!child) return;

        setSelectedChild(child);

        const existingItem = newOrder.items.find(
            (i: any) => i.productId === childId
        );

        if (existingItem) {
            setQuantity(existingItem.quantity);
            setNewQuantity(existingItem.quantity);
        } else {
            setQuantity(null);
            setNewQuantity(1);
        }
    };

    const handleParentSelect = () => {
        setSelectedChild(null);

        const productOrderInfo = newOrder.items.find(
            (i: any) => i.productId === product.id
        );

        if (productOrderInfo) {
            setQuantity(productOrderInfo.quantity);
            setNewQuantity(productOrderInfo.quantity);
        } else {
            setQuantity(null);
            setNewQuantity(1);
        }
    };

    const handleSubmit = () => {
        const targetId = selectedChild ? selectedChild.id : product.id;
        const targetName = selectedChild
            ? selectedChild.name
            : product.name;

        if (!quantity) {
            onAddProduct(targetId, targetName, newQuantity);
        } else {
            const currentItemId = selectedChild ? selectedChild.id : product.id;
            onQuantityChange(newQuantity || 0, currentItemId);
            if (newQuantity === 0) setNewQuantity(1);
        }
        onClose();
    };

    const priceToShow = selectedChild ? selectedChild.price : product?.price;
    const unityToShow = selectedChild ? selectedChild.unity : product?.unity;
    
    // Correction pour l'affichage des images
    const getImageUrl = (item: any) => {
        if (Array.isArray(item?.imageUrls) && item.imageUrls.length > 0) {
            return item.imageUrls[0];
        }
        if (item?.imageUrl) {
            return item.imageUrl;
        }
        return null;
    };
    
    const imageToShow = selectedChild 
        ? getImageUrl(selectedChild)
        : getImageUrl(product);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4">
            {/* Overlay */}
            <div 
                className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal - Taille réduite */}
            <div className="relative z-[1002] w-full max-w-xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl">
                {/* Header avec image de fond - Réduit */}
                <div className="relative h-44 bg-gradient-to-br from-blue-50 to-slate-100 overflow-hidden">
                    <button
                        onClick={onClose}
                        className="absolute top-3 right-3 z-10 p-1.5 bg-white/90 backdrop-blur-sm hover:bg-white rounded-full shadow-lg transition-all hover:scale-110"
                    >
                        <IoMdClose size={20} className="text-slate-700" />
                    </button>

                    {/* Image produit */}
                    <div className="absolute inset-0 flex items-center justify-center p-4">
                        {imageToShow ? (
                            <img
                                src={imageToShow}
                                className="max-h-32 max-w-full object-contain drop-shadow-2xl"
                                alt={selectedChild?.name || product?.name}
                                onError={(e: any) => {
                                    e.target.src = getIcon(product?.name, 'product');
                                }}
                            />
                        ) : (
                            <img
                                className="max-h-32 max-w-full object-contain drop-shadow-2xl"
                                src={getIcon(product?.name, 'product')}
                                alt={product?.name}
                            />
                        )}
                    </div>

                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
                </div>

                {/* Contenu - Espacement réduit */}
                <div className="px-4 pb-4 -mt-6 relative z-10">
                    {/* Titre et prix - Compact */}
                    <div className="bg-white rounded-xl shadow-lg p-3 mb-4">
                        <h1 className="text-lg font-bold text-slate-800 mb-1">{product?.name}</h1>
                        {!hasChildren && !hidePricesOnShop && (
                            <div className="flex items-baseline gap-2">
                                <span className="text-2xl font-bold text-blue-600">
                                    {product?.price?.toFixed(2)}€
                                </span>
                                <span className="text-sm text-slate-500">/ {product?.unity}</span>
                            </div>
                        )}
                    </div>

                    {/* Sélecteur de conditionnement - Compact */}
                    {hasChildren && (
                        <div className="mb-4">
                            <label className="block text-xs font-semibold text-slate-700 mb-2">
                                Choisir le conditionnement
                            </label>
                            <div className="space-y-2">
                                {/* Option parent */}
                                <button
                                    onClick={handleParentSelect}
                                    className={`w-full flex items-center justify-between rounded-xl border-2 p-3 transition-all ${
                                        !selectedChild
                                            ? 'border-blue-600 bg-blue-50 shadow-md scale-[1.02]'
                                            : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                                    }`}
                                >
                                    <div className="text-left flex-1 min-w-0">
                                        <p className={`font-semibold text-sm truncate ${!selectedChild ? 'text-blue-900' : 'text-slate-800'}`}>
                                            {product?.name}
                                        </p>
                                        <p className="text-xs text-slate-600 mt-0.5">
                                            {product?.unity}
                                            {product?.subUnity && product?.subUnitQuantity && (
                                                <span className="ml-1">
                                                    ({product.subUnitQuantity} {product.subUnity})
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                    {!hidePricesOnShop && (
                                        <div className="text-right ml-2">
                                            <p className={`text-lg font-bold ${!selectedChild ? 'text-blue-600' : 'text-slate-800'}`}>
                                                {product?.price?.toFixed(2)}€
                                            </p>
                                        </div>
                                    )}
                                </button>

                                {/* Options children */}
                                {product?.children
                                    ?.filter((child: any) => child.price > 0)
                                    .map((child: any) => (
                                        <button
                                            key={child.id}
                                            onClick={() => handleChildSelect(child.id)}
                                            className={`w-full flex items-center justify-between rounded-xl border-2 p-3 transition-all ${
                                                selectedChild?.id === child.id
                                                    ? 'border-blue-600 bg-blue-50 shadow-md scale-[1.02]'
                                                    : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                                            }`}
                                        >
                                            <div className="text-left flex-1 min-w-0">
                                                <p className={`font-semibold text-sm truncate ${selectedChild?.id === child.id ? 'text-blue-900' : 'text-slate-800'}`}>
                                                    {child.name}
                                                </p>
                                                <p className="text-xs text-slate-600 mt-0.5">
                                                    {child.unity}
                                                    {child.subUnity && child.subUnitQuantity && (
                                                        <span className="ml-1">
                                                            ({child.subUnitQuantity} {child.subUnity})
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                            {!hidePricesOnShop && (
                                                <div className="text-right ml-2">
                                                    <p className={`text-lg font-bold ${selectedChild?.id === child.id ? 'text-blue-600' : 'text-slate-800'}`}>
                                                        {child.price.toFixed(2)}€
                                                    </p>
                                                </div>
                                            )}
                                        </button>
                                    ))}
                            </div>
                        </div>
                    )}

                    {/* Sélecteur de quantité - Compact */}
                    <div className="bg-slate-50 rounded-xl p-3 mb-4">
                        <label className="block text-xs font-semibold text-slate-700 mb-2">
                            Quantité
                        </label>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setNewQuantity(Math.max(0, newQuantity - 1))}
                                className="w-10 h-10 flex items-center justify-center bg-white hover:bg-slate-100 rounded-lg border-2 border-slate-200 transition-all hover:scale-105"
                            >
                                <AiOutlineMinus size={16} className="text-slate-700" />
                            </button>

                            <div className="flex-1 relative">
                                <input
                                    value={newQuantity.toString()}
                                    min={0}
                                    onChange={(e) =>
                                        setNewQuantity(e.target.value ? parseFloat(e.target.value) : 0)
                                    }
                                    type="number"
                                    className="w-full h-10 text-center text-lg font-bold rounded-lg border-2 border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">
                                    {unityToShow}
                                </span>
                            </div>

                            <button
                                onClick={() => setNewQuantity(newQuantity + 1)}
                                className="w-10 h-10 flex items-center justify-center bg-blue-600 hover:bg-blue-700 rounded-lg transition-all hover:scale-105 shadow-lg"
                            >
                                <AiOutlinePlus size={16} className="text-white" />
                            </button>
                        </div>

                        {/* Prix total - Compact */}
                        {!hidePricesOnShop && (
                            <div className="mt-3 flex items-center justify-between p-2.5 bg-white rounded-lg border-2 border-blue-100">
                                <span className="text-sm text-slate-700 font-semibold">Total HT</span>
                                <span className="text-xl font-bold text-blue-600">
                                    {(newQuantity * priceToShow).toFixed(2)} €
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Boutons d'action - Compact */}
                    <div className="flex gap-2">
                        {quantity && (
                            <button
                                onClick={onClose}
                                className="flex-1 py-2.5 rounded-lg border-2 border-slate-200 text-sm text-slate-700 font-semibold hover:bg-slate-50 transition-all"
                            >
                                Annuler
                            </button>
                        )}
                        <button
                            onClick={() => {
                                if ((!quantity && newQuantity > 0) || quantity) handleSubmit();
                            }}
                            disabled={!quantity && newQuantity === 0}
                            className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                                !newQuantity || newQuantity === 0
                                    ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg hover:shadow-xl'
                                    : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl'
                            } ${(!quantity && newQuantity === 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {!quantity ? 'Ajouter au panier' : newQuantity > 0 ? 'Modifier la quantité' : 'Supprimer du panier'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductViewModal;